import { Command } from "commander";
import { getStaticCandidates, SHELL_SCRIPTS } from "../completion.ts";
import {
  CONFIG_KEYS,
  resolveDomains,
  resolveFlowIds,
  resolveSessionIds,
  resolveSkillNames,
} from "./complete-resolvers.ts";

/**
 * Creates the `skaile complete` command.
 *
 * Provides shell tab-completion support. With `--shell zsh|bash|fish` outputs
 * a shell script for `eval`. With `-- <cword> <words...>` returns newline-
 * separated completion candidates at tab time.
 *
 * @param program - The root Commander program instance (used for static tree walk).
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#complete
 */
export function makeCompleteCommand(program: Command): Command {
  const cmd = new Command("complete")
    .description("Generate shell completion")
    .addHelpText("before", 'Usage: eval "$(skaile complete --shell zsh)"\n')
    .option("--shell <shell>", "Output completion script for shell (zsh|bash|fish)")
    .passThroughOptions()
    .argument("[args...]", "Inline completion args: <cword> <words...>")
    .action((args: string[], opts) => {
      // Shell script output mode
      if (opts.shell) {
        const gen = SHELL_SCRIPTS[opts.shell];
        if (!gen) {
          process.stderr.write(`Unknown shell: ${opts.shell}\n`);
          process.exit(1);
        }
        process.stdout.write(`${gen("skaile")}\n`);
        return;
      }

      // Inline completion mode: skaile complete -- <cword> <words...>
      // Commander passes everything after '--' as the variadic args
      if (args.length >= 1) {
        const ci = parseInt(args[0], 10);
        const allWords = args.slice(1);

        // Try dynamic resolution based on context
        const dynamic = resolveDynamic(allWords, ci);
        if (dynamic !== null) {
          process.stdout.write(`${dynamic.join("\n")}\n`);
          return;
        }

        // Fall back to static commander tree walk
        const candidates = getStaticCandidates(program, allWords);
        process.stdout.write(`${candidates.join("\n")}\n`);
        return;
      }

      // No args — show help
      cmd.help();
    });

  return cmd;
}

function resolveDynamic(words: string[], cword: number): string[] | null {
  // words[0] = 'skaile', words[1] = command, etc.
  const cmd = words[1];
  const sub = words[2];
  const isLast = (i: number) => cword === i;

  if (cmd === "run" && isLast(2)) return resolveFlowIds();
  if (cmd === "flow" && sub === "show" && isLast(3)) return resolveFlowIds();
  if (cmd === "skill" && sub === "show" && isLast(3)) return resolveSkillNames();
  if (cmd === "skill" && sub === "list" && isLast(3)) return resolveDomains();
  if (cmd === "session" && ["show", "switch", "delete"].includes(sub!) && isLast(3)) {
    return resolveSessionIds(process.cwd());
  }
  if (cmd === "config" && ["get", "set"].includes(sub!) && isLast(3)) return CONFIG_KEYS;

  return null;
}
